<template>
    <Card class="mb-6">
        <template #header>
            <div class="px-6 py-4 border-b border-gray-100">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center">
                            <i class="pi pi-building text-gray-600 text-base"></i>
                        </div>
                        <div>
                            <h3 class="text-base font-semibold text-gray-900">{{ $t('Client Information') }}</h3>
                            <p class="text-xs text-gray-500 mt-0.5">{{ client?.first_name + ' ' + client?.last_name || client?.company_name || client?.name }}</p>
                        </div>
                    </div>
                    <Button
                        v-if="showViewButton"
                        :label="$t('View Details')"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        size="small"
                        text
                        class="text-gray-600 hover:text-gray-900"
                        @click="$emit('view-client-details')"
                    />
                </div>
            </div>
        </template>

        <template #content>
            <div class="px-6 py-5">
                <!-- Basic Information -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 mb-6">
                    <div>
                        <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            {{ $t('Client Name') }}
                        </dt>
                        <dd class="text-sm text-gray-900">
                            {{ client?.first_name + ' ' + client?.last_name || client?.company_name || client?.name }}
                        </dd>
                    </div>
                    
                    <div>
                        <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            {{ $t('Email') }}
                        </dt>
                        <dd class="text-sm text-gray-900 break-all">
                            {{ client?.email || '—' }}
                        </dd>
                    </div>

                    <div>
                        <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            {{ $t('VAT Number') }}
                        </dt>
                        <dd class="text-sm text-gray-900">
                            {{ client?.invoice_vat_number || '—' }}
                        </dd>
                    </div>
                </div>

                <!-- Address Section -->
                <div class="pt-5 border-t border-gray-100">
                    <h4 class="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-4">
                        {{ $t('Address Information') }}
                    </h4>
                    
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
                        <div>
                            <dt class="text-xs text-gray-500 mb-1">{{ $t('Street') }}</dt>
                            <dd class="text-sm text-gray-900">
                                {{ client?.address_street_name || '—' }}
                            </dd>
                        </div>

                        <div>
                            <dt class="text-xs text-gray-500 mb-1">{{ $t('Number') }}</dt>
                            <dd class="text-sm text-gray-900">
                                {{ client?.address_number || '—' }}
                            </dd>
                        </div>

                        <div>
                            <dt class="text-xs text-gray-500 mb-1">{{ $t('Zipcode') }}</dt>
                            <dd class="text-sm text-gray-900">
                                {{ client?.address_zipcode || '—' }}
                            </dd>
                        </div>

                        <div>
                            <dt class="text-xs text-gray-500 mb-1">{{ $t('State') }}</dt>
                            <dd class="text-sm text-gray-900">
                                {{ client?.address_state || '—' }}
                            </dd>
                        </div>

                        <div>
                            <dt class="text-xs text-gray-500 mb-1">{{ $t('Country') }}</dt>
                            <dd class="text-sm text-gray-900">
                                {{ client?.address_country || '—' }}
                            </dd>
                        </div>
                    </div>

                    <!-- Complete Address Badge -->
                    <div v-if="fullAddress" class="mt-4 pt-4 border-t border-gray-50">
                        <div class="flex items-start gap-2 text-xs text-gray-600">
                            <i class="pi pi-map-marker text-gray-400 mt-0.5"></i>
                            <span class="leading-relaxed">{{ fullAddress }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </Card>
</template>

<script setup>
import { getCurrentInstance, computed } from 'vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    client: {
        type: Object,
        default: () => ({})
    },
    showViewButton: {
        type: Boolean,
        default: true
    }
});

defineEmits(['view-client-details']);

const fullAddress = computed(() => {
    const addressParts = [
        props.client?.address_street_name,
        props.client?.address_number,
        props.client?.address_state,
        props.client?.address_country,
        props.client?.address_zipcode
    ].filter(Boolean);
    
    return addressParts.length > 0 ? addressParts.join(', ') : null;
});
</script>